import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotificationFeed, markNotificationRead } from '../services/notificationApi';

const POLL_INTERVAL_MS = 30000;
const REMINDER_EVENT_TYPE = 'APPOINTMENT_REMINDER_30MIN';
const MAX_VISIBLE_CARDS = 3;

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

/**
 * Floating bottom-right card(s) reminding the current user (patient OR doctor
 * — the backend already scopes /api/notifications to the caller) that they
 * have an appointment starting in ~30 minutes.
 *
 * Source of truth: existing NotificationService / AppointmentReminderScheduler
 * pipeline. This component does not create reminders, it just surfaces the
 * APPOINTMENT_REMINDER_30MIN notifications that already land in the feed as
 * a more visible popup instead of leaving them silently in the bell dropdown.
 *
 * Dismissing a card only hides it for the rest of this browser session (in-memory
 * state — cleared on full reload). It does NOT mark the notification read, so
 * the bell badge count is unaffected. Clicking "View" navigates + marks read,
 * mirroring NotificationBell's existing behavior.
 */
const NearbyAppointmentAlert = () => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const dismissedIds = useRef(new Set());

  const poll = useCallback(() => {
    getNotificationFeed(0, 10, 'APPOINTMENT')
      .then((data) => {
        const list = (data.notifications || []).filter(
          (n) => !n.isRead
            && n.eventType === REMINDER_EVENT_TYPE
            && !dismissedIds.current.has(n.id)
        );
        setReminders(list.slice(0, MAX_VISIBLE_CARDS));
      })
      .catch(() => { /* silent — popup just won't refresh this cycle */ });
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [poll]);

  const handleDismiss = (id) => {
    dismissedIds.current.add(id);
    setReminders((list) => list.filter((n) => n.id !== id));
  };

  const handleView = async (n) => {
    dismissedIds.current.add(n.id);
    setReminders((list) => list.filter((item) => item.id !== n.id));
    try { await markNotificationRead(n.id); } catch { /* best-effort */ }
    if (n.actionUrl) navigate(n.actionUrl);
  };

  if (reminders.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed', bottom: 20, right: 20, zIndex: 9998,
        display: 'flex', flexDirection: 'column', gap: 10,
        width: 320, maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {reminders.map((n) => (
        <div
          key={n.id}
          style={{
            background: '#fff', borderRadius: 14, padding: '14px 16px',
            boxShadow: '0 8px 28px rgba(15,10,40,0.16), 0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #e0e7ff',
            display: 'flex', gap: 12, alignItems: 'flex-start',
            animation: 'dropIn .25s ease both',
          }}
        >
          <div style={{
            flexShrink: 0, width: 34, height: 34, borderRadius: '50%',
            background: '#eef2ff', color: '#4f46e5',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ClockIcon />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 13.5,
              color: '#1e1b4b', marginBottom: 3,
            }}>
              {n.title || 'Upcoming appointment'}
            </div>
            <div style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: '#6b7280',
              lineHeight: 1.5, marginBottom: 10,
            }}>
              {n.body}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {n.actionUrl && (
                <button
                  onClick={() => handleView(n)}
                  style={{
                    background: '#4f46e5', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '6px 12px', fontSize: 12,
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  {n.actionLabel || 'View'}
                </button>
              )}
              <button
                onClick={() => handleDismiss(n.id)}
                style={{
                  background: 'transparent', color: '#6b7280', border: 'none',
                  borderRadius: 8, padding: '6px 10px', fontSize: 12,
                  fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>

          <button
            onClick={() => handleDismiss(n.id)}
            aria-label="Dismiss"
            style={{
              flexShrink: 0, background: 'transparent', border: 'none',
              cursor: 'pointer', color: '#9ca3af', padding: 2,
            }}
          >
            <CloseIcon />
          </button>
        </div>
      ))}
    </div>
  );
};

export default NearbyAppointmentAlert;